import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/*
 * SellerSetupContext — gates the seller portal until the seller has filled
 * out their bare-minimum operating settings (lead time, QI window, warehouse,
 * default terms, restocking policy, location).
 *
 * Until `status.complete === true`, the sidebar locks all non-settings items
 * and the SellerSetupGate redirects any non-settings route to /seller/settings.
 *
 * Once the seller hits "Complete setup" on the Settings page (server-validated),
 * `complete` flips to true permanently — even if they later clear a field, we
 * never re-lock the portal.
 *
 * Backend: GET /api/seller/setup-status, POST /api/seller/setup-status/complete
 */
const SellerSetupContext = createContext({
  status: null,
  loading: false,
  refresh: async () => {},
  complete: async () => {},
});

export function SellerSetupProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    // Cross-role team members (e.g. a buyer who's been invited as an
    // Operator at a seller's team) also need the setup status to render
    // the gate / dashboard correctly. We accept them by checking the
    // team_memberships array surfaced by /auth/me + /auth/login.
    const isSeller = user && user.role === "seller";
    const isCrossRoleSeller = !!(user && user.team_memberships || []).some(
      (m) => m.owner_role === "seller" && m.status === "active",
    );
    if (!user || (!isSeller && !isCrossRoleSeller)) {
      setStatus(null);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/seller/setup-status");
      setStatus(r.data);
    } catch {
      // On error we fail OPEN (treat as complete) — don't lock the portal
      // because of a flaky network call. Also handles 403 for cross-role
      // team members who don't have seller-scoped read access yet —
      // we just unlock the portal for them.
      setStatus({ complete: true, steps: [], can_complete: false, completed_steps: 0, total_steps: 0 });
    } finally {
      setLoading(false);
    }
  }, [user]);

  const complete = useCallback(async () => {
    const r = await api.post("/seller/setup-status/complete");
    await refresh();
    return r.data;
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SellerSetupContext.Provider value={{ status, loading, refresh, complete }}>
      {children}
    </SellerSetupContext.Provider>
  );
}

export function useSellerSetup() {
  return useContext(SellerSetupContext);
}
